import {
  FormEvent,
  ReactElement,
  useContext,
  useState,
  ChangeEventHandler,
} from "react";
import { Form, FormField, TextInput } from "@justworkshr/milo-form";
import { Button } from "@justworkshr/milo-core";

import { Factor } from "types/factor/factor";
import { OktaError } from "types/oktaError/oktaError";

import { MFA_FACTOR_COPY } from "../../constants/factorCopy";
import { addFactorMessage } from "../../constants/pageAlertsCopy";

import PageAlertContext from "contexts/PageAlertContext";
import AuthTransactionContext from "pages/register/contexts/AuthTransactionContext";
import DisableFactorSetupContext from "../../contexts/DisableFactorSetupContext";

import useActivateFactor from "hooks/useActivateFactor";
import useAuthActivateFactor from "pages/register/hooks/useAuthActivateFactor";

import styles from "./auth-code-input.module.css";

const AuthCodeInput = ({
  factor,
  onCancel,
  isCancellingAuthEnrollment = false,
}: {
  factor: Factor;
  onCancel: () => void;
  isCancellingAuthEnrollment: boolean;
}): ReactElement => {
  const { setAlert } = useContext(PageAlertContext);
  const { transaction, setTransaction } = useContext(AuthTransactionContext);
  const { setDisableFactorSetup } = useContext(DisableFactorSetupContext);

  const [authCodeInput, setAuthCodeInput] = useState("");
  const [inlineError, setInlineError] = useState("");
  const clearInlineError = () => setInlineError("");

  const { mutate: activateFactor, isLoading: isActivateFactorLoading } =
    useActivateFactor();
  const { mutate: authActivateFactor, isLoading: isAuthActivateFactorLoading } =
    useAuthActivateFactor();
  const isLoading = transaction
    ? isAuthActivateFactorLoading
    : isActivateFactorLoading;

  const copy = MFA_FACTOR_COPY[factor.key];

  const handleChangeInput: ChangeEventHandler<HTMLInputElement> = (e) => {
    clearInlineError();
    setAuthCodeInput(e.target.value);
  };

  const onSubmitAuthCode = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearInlineError();

    const handleAuthCodeError = (errorResponse: OktaError) => {
      const DEFAULT_ERROR_MESSAGE = "Invalid Passcode/Answer";

      // TODO: Update with problemDetails solution
      setInlineError(errorResponse?.errorSummary ?? DEFAULT_ERROR_MESSAGE);
    };

    if (transaction) {
      authActivateFactor(
        {
          stateToken: transaction.data.stateToken,
          passCode: authCodeInput,
        },
        {
          onSuccess: (transaction) => {
            setTransaction(transaction);
            setDisableFactorSetup(false);
          },
          onError: handleAuthCodeError,
        },
      );
    } else {
      activateFactor(
        {
          factorId: factor.id,
          passCode: authCodeInput,
        },
        {
          onSuccess: () =>
            setAlert({
              message: addFactorMessage(factor.key),
              color: "additive",
            }),
          onError: handleAuthCodeError,
        },
      );
    }
  };

  return (
    <Form onSubmit={onSubmitAuthCode}>
      <FormField
        name="authCodeForm"
        label="Enter the verification code"
        error={inlineError}
        required={true}
      >
        <>
          <div className={styles.inputDescription}>{copy.authCodeMessage}</div>
          <TextInput
            name="authCodeInput"
            value={authCodeInput}
            onChange={handleChangeInput}
          />
        </>
      </FormField>

      <div className={styles.footerButtons}>
        <Button
          key="submit-button"
          type="submit"
          loading={isLoading}
          disabled={!authCodeInput}
        >
          Enable
        </Button>
        <Button
          key="cancel-button"
          variant="ghost"
          onClick={onCancel}
          loading={isCancellingAuthEnrollment}
        >
          Cancel
        </Button>
      </div>
    </Form>
  );
};

export default AuthCodeInput;
